// systems/corruption.js
// Corruption mechanic - rare proc that provides powerful temporary buffs
// Multiple skills can be corrupted simultaneously

import { gameState } from '../game/gameState.js';
import logger from '../utils/logger.js';
import { toastSuccess } from '../ui/toast.js';
import { IS_DEV } from '../config/env.js';
import { hasMastery } from './experience.js';
import { SKILLS } from '../game/constants.js';

/**
 * Corruption Configuration
 */
export const CORRUPTION_CONFIG = {
    // Base proc chance (0.2% = 0.002) for levels 1-98
    BASE_PROC_CHANCE: 0.002,

    // Level 99 proc chance (2% = 0.02) for levels 99-119
    LEVEL_99_PROC_CHANCE: 0.02,

    // Mastery proc chance at level 120 (5% = 0.05)
    MASTERY_PROC_CHANCE: 0.05,

    // Base duration in ms (1 minute)
    BASE_DURATION_MS: 60000,

    // Extension on re-proc in ms (10 seconds)
    EXTENSION_MS: 10000,

    // Maximum duration in ms (5 minutes)
    MAX_DURATION_MS: 300000,

    // Buff values
    SPEED_MULTIPLIER: 1.4,      // 40% faster
    LOOT_MULTIPLIER: 2.0,       // Double loot
    XP_MULTIPLIER: 1.5,         // 50% more XP
    RESOURCE_SAVE_CHANCE: 0.25, // 25% chance to save resources
    GOLD_COST_WAIVED: true,     // Gold costs waived
};

/**
 * Arcane Surge Configuration (Glyphbinding capstone talent)
 * Transforms corruption into a shorter but 3x more powerful burst
 */
export const ARCANE_SURGE_CONFIG = {
    DURATION_MULTIPLIER: 0.4,  // 40% of normal duration (24s base)
    STAT_MULTIPLIER: 3.0,      // 3x all stat bonuses
    PROC_CHANCE_BONUS: 0.02,   // +2% corruption proc chance
};

/**
 * Combat Corruption Configuration ("Corrupted Prey")
 * Unlike other skills, combat corruption debuffs the monster rather than buffing the player.
 * Temporary effects fall off when timer expires; persistent marks survive timer but clear on zone switch.
 */
export const COMBAT_CORRUPTION_CONFIG = {
    // Temporary debuffs (fall off with timer)
    DAMAGE_TAKEN_MULTIPLIER: 1.3,     // +30% damage taken by mob
    DEFENSE_REDUCTION: 0.25,           // -25% mob defense
    GLYPH_SAVE_CHANCE: 0.25,          // 25% chance to save glyphs

    // Persistent marks (survive timer, clear on zone switch)
    LOOT_MULTIPLIER: 2.0,             // 2x loot
    XP_MULTIPLIER: 1.5,               // 1.5x XP
};

/**
 * Check if a skill has the Arcane Surge talent active
 * (Glyphbinding capstone - transforms corruption)
 * @param {string} skillId - The skill to check
 * @returns {boolean}
 */
function hasArcaneSurge(skillId) {
    if (skillId !== SKILLS.GLYPHBINDING) return false;

    // Check if the Arcane Conduit capstone is purchased via talent tree state
    const tree = gameState.getTalentTree(SKILLS.GLYPHBINDING);
    if (!tree || !tree.nodes) return false;

    const conduitRank = tree.nodes['glyphbinding_conduit'] || 0;
    return conduitRank > 0;
}

/**
 * Skills that can be affected by corruption
 * Excludes time-based passive skills:
 * - farming (crop growth)
 * - architecting (blueprints/milestones)
 * - husbandry (pet contracts)
 * - bounty (quests)
 */
export const CORRUPTIBLE_SKILLS = [
    'mining',
    'woodcutting',
    'fishing',
    'smithing',
    'cooking',
    'pyre',
    'crafting',
    'woodworking',
    'glyphbinding',
    'alchemy',
    'pilfering',
    'gatherer',
    'combat',
];

/**
 * Check if a skill can be affected by corruption
 * @param {string} skillId - The skill to check
 * @returns {boolean}
 */
export function isCorruptibleSkill(skillId) {
    return CORRUPTIBLE_SKILLS.includes(skillId);
}

/**
 * Get corruption state for a specific skill
 * @param {string} skillId - The skill to check
 * @returns {Object} - Skill corruption state or null
 */
function getSkillCorruptionState(skillId) {
    const corruption = gameState.getCorruption();
    return corruption.skills?.[skillId] || null;
}

// Test-determinism hook: when disabled, rollForCorruption never procs. Corruption
// is a 0.2% RNG event; left active across the suite it bleeds random XP/loot
// multipliers into deterministic test assertions. resetGameState() sets this true
// in the test environment. Production never disables it.
let corruptionRollsDisabled = false;
export function setCorruptionRollsDisabled(disabled) {
    corruptionRollsDisabled = disabled;
}

/**
 * Check if corruption should proc on action completion
 * @param {string} skillId - The skill that completed an action
 * @returns {boolean} - Whether corruption procced
 */
export function rollForCorruption(skillId, bonusProcChance = 0) {
    // Only corruptible skills can trigger corruption
    if (!isCorruptibleSkill(skillId)) {
        return false;
    }

    // Test determinism: skip the random roll entirely when disabled.
    if (corruptionRollsDisabled) {
        return false;
    }

    const skillState = getSkillCorruptionState(skillId);

    // Don't proc if already at max duration
    if (skillState?.active && skillState.remainingMs >= CORRUPTION_CONFIG.MAX_DURATION_MS) {
        return false;
    }

    // Check for admin override (import dynamically to avoid circular dep)
    let procChance;
    if (IS_DEV && typeof window !== 'undefined') {
        // Dynamic import to avoid circular dependency
        const adminOverride = window.adminPanel?.getCorruptionProcChance?.();
        if (adminOverride !== null && adminOverride !== undefined) {
            procChance = adminOverride;
        }
    }

    // If no admin override, use default proc chance based on skill level
    if (procChance === undefined) {
        const skillData = gameState.getSkill(skillId);
        const virtualLevel = skillData?.virtualLevel || skillData?.level || 1;

        // Tiered proc chances: 0.2% base, 2% at 99, 5% at 120
        if (virtualLevel >= 120) {
            procChance = CORRUPTION_CONFIG.MASTERY_PROC_CHANCE; // 5%
        } else if (virtualLevel >= 99) {
            procChance = CORRUPTION_CONFIG.LEVEL_99_PROC_CHANCE; // 2%
        } else {
            procChance = CORRUPTION_CONFIG.BASE_PROC_CHANCE; // 0.2%
        }

        // Arcane Surge talent (Glyphbinding capstone) increases proc chance
        if (hasArcaneSurge(skillId)) {
            procChance += ARCANE_SURGE_CONFIG.PROC_CHANCE_BONUS;
        }

        // Add bonus proc chance (e.g., from Ascendant Concoction potion)
        if (bonusProcChance > 0) {
            procChance += bonusProcChance;
        }
    }

    // Roll for corruption
    const roll = Math.random();
    if (roll < procChance) {
        activateCorruption(skillId);
        return true;
    }

    return false;
}

/**
 * Activate or extend corruption buff for a specific skill
 * @param {string} skillId - The skill to corrupt
 */
export function activateCorruption(skillId) {
    const corruption = gameState.getCorruption();
    const skillState = corruption.skills?.[skillId];
    const isArcaneSurge = hasArcaneSurge(skillId);

    // Calculate durations based on Arcane Surge
    const baseDuration = isArcaneSurge
        ? Math.floor(CORRUPTION_CONFIG.BASE_DURATION_MS * ARCANE_SURGE_CONFIG.DURATION_MULTIPLIER)
        : CORRUPTION_CONFIG.BASE_DURATION_MS;
    const extensionDuration = isArcaneSurge
        ? Math.floor(CORRUPTION_CONFIG.EXTENSION_MS * ARCANE_SURGE_CONFIG.DURATION_MULTIPLIER)
        : CORRUPTION_CONFIG.EXTENSION_MS;
    const maxDuration = isArcaneSurge
        ? Math.floor(CORRUPTION_CONFIG.MAX_DURATION_MS * ARCANE_SURGE_CONFIG.DURATION_MULTIPLIER)
        : CORRUPTION_CONFIG.MAX_DURATION_MS;

    if (skillState?.active) {
        // Extend duration (up to max)
        const newDuration = Math.min(
            skillState.remainingMs + extensionDuration,
            maxDuration
        );
        gameState.setSkillCorruption(skillId, {
            active: true,
            startedAt: skillState.startedAt,
            remainingMs: newDuration,
            extended: true,
            arcaneSurge: isArcaneSurge,
        });
        if (isArcaneSurge) {
            logger.game(`✨ Arcane Surge extended! (+${extensionDuration / 1000}s)`);
            toastSuccess(`✨ Arcane Surge Extended! +${extensionDuration / 1000}s`);
        } else {
            logger.game(`⚡ Corruption extended on ${skillId}! (+${extensionDuration / 1000}s)`);
            toastSuccess(`⚡ ${skillId} Corruption Extended! +${extensionDuration / 1000}s`);
        }
    } else {
        // Activate new corruption on this skill
        gameState.setSkillCorruption(skillId, {
            active: true,
            startedAt: Date.now(),
            remainingMs: baseDuration,
            extended: false,
            arcaneSurge: isArcaneSurge,
        });
        if (isArcaneSurge) {
            logger.game(`✨ ARCANE SURGE ACTIVATED! (${baseDuration / 1000}s)`);
            toastSuccess(`✨ ARCANE SURGE! +150% XP · +120% Speed · 6x Loot · ${baseDuration / 1000}s`);
        } else {
            logger.game(`⚡ CORRUPTION ACTIVATED on ${skillId}! (${baseDuration / 1000}s)`);
            toastSuccess(`⚡ ${skillId.toUpperCase()} CORRUPTED! +50% XP · +40% Speed · 2x Loot · 60s`);
        }
    }
}

/**
 * Force activate corruption for testing (uses current action's skill or specified skill)
 * @param {number} durationMs - Duration in ms (default: BASE_DURATION_MS)
 * @param {string} skillId - Skill to corrupt (default: current action's skill)
 */
export function forceActivateCorruption(durationMs = CORRUPTION_CONFIG.BASE_DURATION_MS, skillId = null) {
    // Use current action's skill if not specified
    if (!skillId) {
        const currentAction = gameState.getCurrentAction();
        skillId = currentAction?.type || 'mining';
    }

    const actualDuration = Math.min(durationMs, CORRUPTION_CONFIG.MAX_DURATION_MS);
    gameState.setSkillCorruption(skillId, {
        active: true,
        startedAt: Date.now(),
        remainingMs: actualDuration,
        extended: false,
    });
    logger.game(`⚡ CORRUPTION FORCED on ${skillId}! (${actualDuration / 1000}s)`);
    toastSuccess(`⚡ ${skillId.toUpperCase()} CORRUPTED! +50% XP · +40% Speed · 2x Loot · ${actualDuration / 1000}s`);
}

/**
 * Deactivate corruption for a specific skill
 * @param {string} skillId - The skill to deactivate corruption for
 */
export function deactivateCorruption(skillId) {
    gameState.setSkillCorruption(skillId, {
        active: false,
        startedAt: null,
        remainingMs: 0,
        extended: false,
    });
    logger.game(`⚡ ${skillId} corruption faded...`);
}

/**
 * Deactivate all corruption
 */
export function deactivateAllCorruption() {
    const corruption = gameState.getCorruption();
    for (const skillId of Object.keys(corruption.skills || {})) {
        if (corruption.skills[skillId]?.active) {
            deactivateCorruption(skillId);
        }
    }
}

/**
 * Update corruption timers for all skills (called every tick)
 * @param {number} deltaMs - Time elapsed since last tick
 * @returns {Array<string>} - Skills that just expired
 */
export function updateCorruptionTimer(deltaMs) {
    const corruption = gameState.getCorruption();
    const expiredSkills = [];

    for (const skillId of Object.keys(corruption.skills || {})) {
        const skillState = corruption.skills[skillId];
        if (!skillState?.active) continue;

        const newRemaining = skillState.remainingMs - deltaMs;

        if (newRemaining <= 0) {
            deactivateCorruption(skillId);
            expiredSkills.push(skillId);
        } else {
            gameState.setSkillCorruption(skillId, {
                ...skillState,
                remainingMs: newRemaining,
            });
        }
    }

    return expiredSkills;
}

/**
 * Check if corruption is currently active (any skill)
 * @returns {boolean}
 */
export function isCorruptionActive() {
    const corruption = gameState.getCorruption();
    for (const skillId of Object.keys(corruption.skills || {})) {
        if (corruption.skills[skillId]?.active) {
            return true;
        }
    }
    return false;
}

/**
 * Check if a specific skill is currently corrupted
 * @param {string} skillId - The skill to check
 * @returns {boolean}
 */
export function isSkillCorrupted(skillId) {
    const skillState = getSkillCorruptionState(skillId);
    return skillState?.active === true;
}

/**
 * Get all currently corrupted skill IDs
 * @returns {Array<string>}
 */
export function getCorruptedSkillIds() {
    const corruption = gameState.getCorruption();
    const corruptedSkills = [];
    for (const skillId of Object.keys(corruption.skills || {})) {
        if (corruption.skills[skillId]?.active) {
            corruptedSkills.push(skillId);
        }
    }
    return corruptedSkills;
}

/**
 * Get corruption state for a specific skill with calculated values
 * @param {string} skillId - The skill to check
 * @returns {Object} - Corruption state with buff values
 */
export function getSkillCorruptionInfo(skillId) {
    const skillState = getSkillCorruptionState(skillId);

    if (!skillState?.active) {
        return {
            active: false,
            skillId: skillId,
            speedMultiplier: 1,
            lootMultiplier: 1,
            xpMultiplier: 1,
            resourceSaveChance: 0,
            goldCostWaived: false,
            remainingMs: 0,
            remainingSeconds: 0,
        };
    }

    return {
        active: true,
        skillId: skillId,
        speedMultiplier: CORRUPTION_CONFIG.SPEED_MULTIPLIER,
        lootMultiplier: CORRUPTION_CONFIG.LOOT_MULTIPLIER,
        xpMultiplier: CORRUPTION_CONFIG.XP_MULTIPLIER,
        resourceSaveChance: CORRUPTION_CONFIG.RESOURCE_SAVE_CHANCE,
        goldCostWaived: CORRUPTION_CONFIG.GOLD_COST_WAIVED,
        remainingMs: skillState.remainingMs,
        remainingSeconds: Math.ceil(skillState.remainingMs / 1000),
        atMaxDuration: skillState.remainingMs >= CORRUPTION_CONFIG.MAX_DURATION_MS,
        extended: skillState.extended || false,
        arcaneSurge: skillState?.arcaneSurge || false,
    };
}

/**
 * Get current corruption state (legacy compatibility - returns first active)
 * @returns {Object} - Corruption state with buff values
 * @deprecated Use getSkillCorruptionInfo(skillId) instead
 */
export function getCorruptionState() {
    const corruptedSkills = getCorruptedSkillIds();
    if (corruptedSkills.length === 0) {
        return {
            active: false,
            skillId: null,
            speedMultiplier: 1,
            lootMultiplier: 1,
            xpMultiplier: 1,
            resourceSaveChance: 0,
            goldCostWaived: false,
            remainingMs: 0,
            remainingSeconds: 0,
        };
    }
    // Return first active corruption for legacy compatibility
    return getSkillCorruptionInfo(corruptedSkills[0]);
}

/**
 * Check if resource should be saved due to corruption (for a specific skill)
 * Mastery doubles the save chance (25% → 50%)
 * @param {string} skillId - The skill to check
 * @returns {boolean}
 */
export function shouldSaveResource(skillId) {
    if (!isSkillCorrupted(skillId)) return false;
    const baseChance = CORRUPTION_CONFIG.RESOURCE_SAVE_CHANCE;
    const effectiveChance = hasMastery(skillId) ? baseChance * 2 : baseChance;
    return Math.random() < effectiveChance;
}

/**
 * Get the speed multiplier for a specific skill
 * Mastery doubles the bonus portion (1.4x → 1.8x)
 * Arcane Surge triples the bonus portion (1.4x → 2.2x)
 * @param {string} skillId - The skill to check
 * @returns {number} - 1.0 if not corrupted, adjusted multiplier if corrupted
 */
export function getCorruptionSpeedMultiplier(skillId) {
    if (skillId === SKILLS.COMBAT) return 1; // Combat uses its own system (Corrupted Prey)
    if (!isSkillCorrupted(skillId)) return 1;

    const skillState = getSkillCorruptionState(skillId);
    const baseMultiplier = CORRUPTION_CONFIG.SPEED_MULTIPLIER;
    const bonusPortion = baseMultiplier - 1;

    // Arcane Surge triples the bonus (stacks with mastery)
    if (skillState?.arcaneSurge) {
        const surgeBonus = bonusPortion * ARCANE_SURGE_CONFIG.STAT_MULTIPLIER;
        if (hasMastery(skillId)) {
            // Mastery doubles on top of surge: 3x * 2 = 6x bonus
            return 1 + surgeBonus * 2;
        }
        return 1 + surgeBonus; // 1 + (0.4 * 3) = 2.2x
    }

    if (hasMastery(skillId)) {
        // Double the bonus: 1.4x → 1 + (0.4 * 2) = 1.8x
        return 1 + bonusPortion * 2;
    }
    return baseMultiplier;
}

/**
 * Get the loot multiplier for a specific skill
 * Mastery doubles the bonus portion (2.0x → 3.0x)
 * Arcane Surge triples the bonus portion (2.0x → 4.0x)
 * @param {string} skillId - The skill to check
 * @returns {number} - 1.0 if not corrupted, adjusted multiplier if corrupted
 */
export function getCorruptionLootMultiplier(skillId) {
    if (skillId === SKILLS.COMBAT) return 1; // Combat uses its own system (Corrupted Prey)
    if (!isSkillCorrupted(skillId)) return 1;

    const skillState = getSkillCorruptionState(skillId);
    const baseMultiplier = CORRUPTION_CONFIG.LOOT_MULTIPLIER;
    const bonusPortion = baseMultiplier - 1;

    // Arcane Surge triples the bonus (stacks with mastery)
    if (skillState?.arcaneSurge) {
        const surgeBonus = bonusPortion * ARCANE_SURGE_CONFIG.STAT_MULTIPLIER;
        if (hasMastery(skillId)) {
            return 1 + surgeBonus * 2;
        }
        return 1 + surgeBonus; // 1 + (1.0 * 3) = 4.0x
    }

    if (hasMastery(skillId)) {
        // Double the bonus: 2.0x → 1 + (1.0 * 2) = 3.0x
        return 1 + bonusPortion * 2;
    }
    return baseMultiplier;
}

/**
 * Get the XP multiplier for a specific skill
 * Mastery doubles the bonus portion (1.5x → 2.0x)
 * Arcane Surge triples the bonus portion (1.5x → 2.5x)
 * @param {string} skillId - The skill to check
 * @returns {number} - 1.0 if not corrupted, adjusted multiplier if corrupted
 */
export function getCorruptionXPMultiplier(skillId) {
    if (skillId === SKILLS.COMBAT) return 1; // Combat uses its own system (Corrupted Prey)
    if (!isSkillCorrupted(skillId)) return 1;

    const skillState = getSkillCorruptionState(skillId);
    const baseMultiplier = CORRUPTION_CONFIG.XP_MULTIPLIER;
    const bonusPortion = baseMultiplier - 1;

    // Arcane Surge triples the bonus (stacks with mastery)
    if (skillState?.arcaneSurge) {
        const surgeBonus = bonusPortion * ARCANE_SURGE_CONFIG.STAT_MULTIPLIER;
        if (hasMastery(skillId)) {
            return 1 + surgeBonus * 2;
        }
        return 1 + surgeBonus; // 1 + (0.5 * 3) = 2.5x
    }

    if (hasMastery(skillId)) {
        // Double the bonus: 1.5x → 1 + (0.5 * 2) = 2.0x
        return 1 + bonusPortion * 2;
    }
    return baseMultiplier;
}

/**
 * Check if gold costs should be waived for a specific skill
 * @param {string} skillId - The skill to check
 * @returns {boolean}
 */
export function isGoldCostWaived(skillId) {
    return isSkillCorrupted(skillId) && CORRUPTION_CONFIG.GOLD_COST_WAIVED;
}

// ==================== COMBAT CORRUPTION (Corrupted Prey) ====================

/**
 * Get damage taken multiplier for mob when combat is corrupted
 * Mastery doubles the bonus portion (1.3x → 1.6x)
 * @returns {number} - 1.0 if not corrupted, 1.3 or 1.6 if corrupted
 */
export function getCombatDamageTakenMult() {
    if (!isSkillCorrupted(SKILLS.COMBAT)) return 1;
    const base = COMBAT_CORRUPTION_CONFIG.DAMAGE_TAKEN_MULTIPLIER;
    const bonus = base - 1;
    return hasMastery(SKILLS.COMBAT) ? 1 + bonus * 2 : base;
}

/**
 * Get mob defense reduction when combat is corrupted
 * Mastery doubles (0.25 → 0.50)
 * @returns {number} - 0 if not corrupted, 0.25 or 0.50 if corrupted
 */
export function getCombatDefenseReduction() {
    if (!isSkillCorrupted(SKILLS.COMBAT)) return 0;
    const base = COMBAT_CORRUPTION_CONFIG.DEFENSE_REDUCTION;
    return hasMastery(SKILLS.COMBAT) ? base * 2 : base;
}

/**
 * Get glyph save chance when combat is corrupted
 * Mastery doubles (0.25 → 0.50)
 * @returns {number} - 0 if not corrupted, 0.25 or 0.50 if corrupted
 */
export function getCombatGlyphSave() {
    if (!isSkillCorrupted(SKILLS.COMBAT)) return 0;
    const base = COMBAT_CORRUPTION_CONFIG.GLYPH_SAVE_CHANCE;
    return hasMastery(SKILLS.COMBAT) ? base * 2 : base;
}

/**
 * Get loot multiplier for Corrupted Prey mark (used when marking mob)
 * Mastery doubles the bonus portion (2.0x → 3.0x)
 * @returns {number} - 2.0 or 3.0
 */
export function getCombatLootMark() {
    const base = COMBAT_CORRUPTION_CONFIG.LOOT_MULTIPLIER;
    const bonus = base - 1;
    return hasMastery(SKILLS.COMBAT) ? 1 + bonus * 2 : base;
}

/**
 * Get XP multiplier for Corrupted Prey mark (used when marking mob)
 * Mastery doubles the bonus portion (1.5x → 2.0x)
 * @returns {number} - 1.5 or 2.0
 */
export function getCombatXPMark() {
    const base = COMBAT_CORRUPTION_CONFIG.XP_MULTIPLIER;
    const bonus = base - 1;
    return hasMastery(SKILLS.COMBAT) ? 1 + bonus * 2 : base;
}

export default {
    CORRUPTION_CONFIG,
    COMBAT_CORRUPTION_CONFIG,
    rollForCorruption,
    activateCorruption,
    forceActivateCorruption,
    deactivateCorruption,
    deactivateAllCorruption,
    updateCorruptionTimer,
    isCorruptionActive,
    isSkillCorrupted,
    getCorruptedSkillIds,
    getSkillCorruptionInfo,
    getCorruptionState,
    shouldSaveResource,
    getCorruptionSpeedMultiplier,
    getCorruptionLootMultiplier,
    getCorruptionXPMultiplier,
    isGoldCostWaived,
    getCombatDamageTakenMult,
    getCombatDefenseReduction,
    getCombatGlyphSave,
    getCombatLootMark,
    getCombatXPMark,
};
